import type { CSSProperties, ReactNode } from 'react';
import { Fragment } from 'react';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { usePanelChrome } from './PanelChromeContext';

export interface TopbarProps {
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumb?: ReactNode[];
  actions?: ReactNode;
  search?: boolean;
  searchPlaceholder?: string;
  /** Hide bell + settings cluster (some screens like the public Login). */
  hideUtilityIcons?: boolean;
  style?: CSSProperties;
  className?: string;
}

const iconBtnStyle = (isMobile: boolean): CSSProperties => ({
  width: isMobile ? 40 : 34,
  height: isMobile ? 40 : 34,
  borderRadius: 6,
  border: '1px solid var(--dg-border)',
  background: 'white',
  color: 'var(--dg-text-2)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export function Topbar({
  title,
  subtitle,
  breadcrumb,
  actions,
  search,
  searchPlaceholder = 'Ticket, müşteri, fatura ara...',
  hideUtilityIcons,
  style,
  className,
}: TopbarProps) {
  const { isMobile, navOpen, setNavOpen } = usePanelChrome();
  return (
    <header
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: isMobile ? '14px 16px' : '14px 24px',
        borderBottom: '1px solid var(--dg-border)',
        background: 'white',
        minHeight: 64,
        ...style,
      }}
    >
      {isMobile && (
        <button
          type="button"
          aria-label="Menü"
          aria-expanded={navOpen}
          onClick={() => setNavOpen(!navOpen)}
          style={iconBtnStyle(true)}
        >
          <Icon name="menu" size={18} />
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {breadcrumb && breadcrumb.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--dg-text-3)',
              marginBottom: 2,
            }}
          >
            {breadcrumb.map((b, i) => (
              <Fragment key={i}>
                {i > 0 && <Icon name="chevright" size={12} />}
                <span
                  style={{
                    color:
                      i === breadcrumb.length - 1 ? 'var(--dg-text-2)' : 'var(--dg-text-3)',
                  }}
                >
                  {b}
                </span>
              </Fragment>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h1
            style={{
              fontSize: 19,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: 'var(--dg-text-1)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </h1>
          {subtitle && !isMobile && <span style={{ fontSize: 13, color: 'var(--dg-text-3)' }}>{subtitle}</span>}
        </div>
      </div>

      {search && !isMobile && (
        <div style={{ width: 280 }}>
          <Input size="sm" icon="search" placeholder={searchPlaceholder} />
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {actions}
        {!hideUtilityIcons && (
          <>
            <button type="button" style={iconBtnStyle(isMobile)} aria-label="Bildirimler">
              <Icon name="bell" size={16} />
            </button>
            <button type="button" style={iconBtnStyle(isMobile)} aria-label="Ayarlar">
              <Icon name="settings" size={16} />
            </button>
          </>
        )}
      </div>
    </header>
  );
}
